import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector } from 'sonata-react-components';

import { canConfirmAddToGroup } from '../../domain/groupRules';
import { useGrouping } from '../../hooks/useGrouping';
import { useGroupingItems } from '../../hooks/useGroupingItems';
import { GroupingModal } from '../GroupingModal/GroupingModal';
import { groupingMessages } from '../groupingMessages';
import { SelectedItemsSummary } from '../SelectedItemsSummary/SelectedItemsSummary';
import { firstOptionValue, toGroupOptions } from '../selectorOptions';

import type { GroupFamily } from '../../domain/grouping.types';
import type { GroupingNotice } from '../groupingNotice.types';

export interface AddToGroupModalProps {
  family: GroupFamily;
  /** UI ids of the selected items to add. */
  itemIds: string[];
  onApplied: (notice: GroupingNotice) => void;
  onClose: () => void;
}

/** "Add to existing group": the user must explicitly pick one of the groups accepting new members (not cartographic). */
export function AddToGroupModal({ family, itemIds, onApplied, onClose }: Readonly<AddToGroupModalProps>) {
  const { formatMessage } = useIntl();
  const grouping = useGrouping(family);
  const items = useGroupingItems(family);
  const [groupId, setGroupId] = useState<string | null>(null);

  const selectedItems = useMemo(() => items.filter(item => itemIds.includes(item.id)), [items, itemIds]);
  const selectedItemIds = selectedItems.map(item => item.id);
  const memberIds = groupId ? grouping.getMemberIds(groupId) : [];
  const group = grouping.addableGroups.find(({ id }) => id === groupId);
  const options = toGroupOptions(grouping.addableGroups);
  const canConfirm = canConfirmAddToGroup({ groupId, memberIds, selectedItemIds });

  const handleConfirm = () => {
    if (!group) {
      return;
    }
    const newItemIds = selectedItemIds.filter(id => !memberIds.includes(id));
    grouping.applyGroupChange({ groupId: group.id, itemIds: newItemIds, type: 'addItems' });
    onApplied({ groupName: group.name, itemCount: newItemIds.length, kind: 'added' });
  };

  return (
    <GroupingModal
      id="addToGroupModal"
      leftButton={
        <ButtonUI
          id="addToGroupModal-cancel"
          onClick={onClose}
          shape="squareRounded"
          text={formatMessage(groupingMessages.cancel)}
          type="secondary"
        />
      }
      onClose={onClose}
      rightButton={
        <ButtonUI
          disabled={!canConfirm}
          icon="fa-solid fa-plus"
          id="addToGroupModal-confirm"
          onClick={handleConfirm}
          shape="squareRounded"
          text={formatMessage(groupingMessages.addToGroup)}
          type="primary"
        />
      }
      title={formatMessage(groupingMessages.existingGroupsTitle)}
    >
      <SelectedItemsSummary family={family} groups={grouping.groups} items={selectedItems} />
      <Selector
        label={formatMessage(groupingMessages.groups)}
        onChange={selected => setGroupId(firstOptionValue(selected))}
        options={options}
        placeholder={formatMessage(groupingMessages.selectPlaceholder)}
        type="simple"
        value={options.filter(option => option.value === groupId)}
      />
    </GroupingModal>
  );
}
